import { shallowRef, type ShallowRef } from 'vue';
import { useIntervalFn } from '@vueuse/core';

/**
 * Polls a getter every `intervalMs` and exposes the latest value as a shallowRef.
 * Use this to bridge a third-party object on `window` into Vue's reactivity.
 *
 * Pass a getter that picks the *specific* value you care about — not the whole
 * widget object. Comparing entire third-party objects (e.g. via JSON.stringify)
 * is slow and unsafe: deeply nested or self-referencing widget state will throw
 * `RangeError: Maximum call stack size exceeded` and crash the page.
 */
export const useExternalWatcher = <T>(getter: () => T | null | undefined, intervalMs = 100): ShallowRef<T | null> => {
  const internalValue = shallowRef<T | null>(null);

  if (import.meta.client) {
    useIntervalFn(() => {
      const current = getter() ?? null;
      if (internalValue.value !== current) {
        internalValue.value = current;
      }
    }, intervalMs);
  }

  return internalValue;
};
